import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { AuthContext } from '../context/AuthContext';
import {
  MdAdd, MdEdit, MdDelete, MdClose, MdUpload, MdImage,
  MdLink, MdCode, MdPublic, MdVisibilityOff, MdOpenInNew,
  MdFolder
} from 'react-icons/md';
import { FaGithub } from 'react-icons/fa';
import './Portfolio.css';

const CATEGORIES = [
  'Web App', 'Mobile App', 'Desktop App', 'API / Backend',
  'UI/UX Design', 'E-commerce', 'Dashboard', 'Other'
];

const emptyForm = {
  title: '',
  description: '',
  category: '',
  tech_stack: '',
  live_url: '',
  github_url: '',
  status: 'published',
  sort_order: 0
};

const Portfolio = () => {
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin';

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [deletingId, setDeletingId] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (user !== null) {
      fetchProjects();
    }
  }, [user]);  // re-run when user loads

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const admin = user?.role === 'admin';
      const endpoint = admin ? '/api/portfolio/all' : '/api/portfolio';
      const res = await axios.get(endpoint);
      setProjects(res.data.projects || []);
    } catch (error) {
      console.error('Error fetching portfolio:', error);
    } finally {
      setLoading(false);
    }
  };

  const showMsg = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 3500);
  };

  const handleOpenCreate = () => {
    setEditingProject(null);
    setFormData(emptyForm);
    setImageFile(null);
    setImagePreview(null);
    setShowModal(true);
  };

  const handleOpenEdit = (project) => {
    setEditingProject(project);
    const tech = Array.isArray(project.tech_stack)
      ? project.tech_stack.join(', ')
      : (project.tech_stack ? JSON.parse(project.tech_stack).join(', ') : '');
    setFormData({
      title: project.title || '',
      description: project.description || '',
      category: project.category || '',
      tech_stack: tech,
      live_url: project.live_url || '',
      github_url: project.github_url || '',
      status: project.status || 'published',
      sort_order: project.sort_order || 0
    });
    setImageFile(null);
    setImagePreview(project.image_url || null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingProject(null);
    setFormData(emptyForm);
    setImageFile(null);
    setImagePreview(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showMsg('error', 'Please select a valid image file.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      showMsg('error', 'Image must be under 10MB.');
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      showMsg('error', 'Title is required.');
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(formData).forEach(([k, v]) => fd.append(k, v));
      if (imageFile) fd.append('photo', imageFile);

      if (editingProject) {
        await axios.put(`/api/portfolio/${editingProject.id}`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        showMsg('success', 'Project updated successfully!');
      } else {
        await axios.post('/api/portfolio', fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        showMsg('success', 'Project created successfully!');
      }
      handleCloseModal();
      fetchProjects();
    } catch (error) {
      console.error('Save error:', error);
      showMsg('error', error.response?.data?.error || 'Failed to save project.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this portfolio project? This cannot be undone.')) return;
    setDeletingId(id);
    try {
      await axios.delete(`/api/portfolio/${id}`);
      showMsg('success', 'Project deleted.');
      setProjects(prev => prev.filter(p => p.id !== id));
    } catch (error) {
      showMsg('error', 'Failed to delete project.');
    } finally {
      setDeletingId(null);
    }
  };

  const parseTech = (tech_stack) => {
    if (!tech_stack) return [];
    if (Array.isArray(tech_stack)) return tech_stack;
    try { return JSON.parse(tech_stack); } catch { return []; }
  };

  return (
    <div className="app-container">
      <Sidebar />
      <div className="main-content portfolio-main">
        <div className="portfolio-page">

          {/* Header */}
          <div className="portfolio-header">
            <div>
              <h1><MdFolder /> Portfolio Projects</h1>
              <p className="portfolio-subtitle">
                {isAdmin
                  ? 'Manage projects displayed on jiruutech.com'
                  : 'Projects built by JiruuTech'}
              </p>
            </div>
            {isAdmin && (
              <button className="btn-add-project" onClick={handleOpenCreate}>
                <MdAdd /> Add Project
              </button>
            )}
          </div>

          {/* Message */}
          {message.text && (
            <div className={`portfolio-message ${message.type}`}>
              {message.text}
            </div>
          )}

          {/* Grid */}
          {loading ? (
            <div className="portfolio-loading">
              <div className="loading-spinner"></div>
              <p>Loading projects...</p>
            </div>
          ) : projects.length === 0 ? (
            <div className="portfolio-empty">
              <MdFolder className="empty-icon" />
              <h3>No Projects Yet</h3>
              {isAdmin
                ? <p>Click "Add Project" to showcase your first project on jiruutech.com</p>
                : <p>No portfolio projects have been added yet.</p>}
            </div>
          ) : (
            <div className="portfolio-grid">
              {projects.map(project => (
                <div key={project.id} className={`portfolio-card ${project.status === 'draft' ? 'draft' : ''}`}>
                  {/* Image */}
                  <div className="portfolio-card-image">
                    {project.image_url ? (
                      <img src={project.image_url} alt={project.title} />
                    ) : (
                      <div className="portfolio-no-image">
                        <MdImage />
                      </div>
                    )}
                    {project.status === 'draft' && (
                      <span className="draft-badge"><MdVisibilityOff /> Draft</span>
                    )}
                    {project.category && (
                      <span className="category-badge">{project.category}</span>
                    )}
                  </div>

                  {/* Content */}
                  <div className="portfolio-card-body">
                    <h3 className="portfolio-card-title">{project.title}</h3>
                    {project.description && (
                      <p className="portfolio-card-desc">{project.description}</p>
                    )}

                    {/* Tech stack */}
                    {parseTech(project.tech_stack).length > 0 && (
                      <div className="tech-tags">
                        {parseTech(project.tech_stack).map((tech, i) => (
                          <span key={i} className="tech-tag">{tech}</span>
                        ))}
                      </div>
                    )}

                    {/* Links */}
                    <div className="portfolio-card-links">
                      {project.live_url && (
                        <a href={project.live_url} target="_blank" rel="noopener noreferrer" className="link-btn live">
                          <MdOpenInNew /> Live
                        </a>
                      )}
                      {project.github_url && (
                        <a href={project.github_url} target="_blank" rel="noopener noreferrer" className="link-btn github">
                          <FaGithub /> GitHub
                        </a>
                      )}
                    </div>

                    {/* Admin actions */}
                    {isAdmin && (
                      <div className="portfolio-card-actions">
                        <button className="card-action-btn edit" onClick={() => handleOpenEdit(project)} title="Edit">
                          <MdEdit />
                        </button>
                        <button
                          className="card-action-btn delete"
                          onClick={() => handleDelete(project.id)}
                          disabled={deletingId === project.id}
                          title="Delete"
                        >
                          <MdDelete />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="portfolio-modal" onClick={e => e.stopPropagation()}>
            <div className="portfolio-modal-header">
              <h2>{editingProject ? 'Edit Project' : 'Add Portfolio Project'}</h2>
              <button className="modal-close-btn" onClick={handleCloseModal}><MdClose /></button>
            </div>

            <form onSubmit={handleSubmit} className="portfolio-form">
              {/* Image upload */}
              <div className="form-group">
                <label>Project Image</label>
                <div className="image-drop-zone" onClick={() => fileRef.current?.click()}>
                  {imagePreview ? (
                    <img src={imagePreview} alt="Preview" className="image-preview" />
                  ) : (
                    <>
                      <MdImage className="drop-icon" />
                      <p>Click to select an image</p>
                      <span>JPG, PNG, WebP — max 10MB</span>
                    </>
                  )}
                </div>
                <input ref={fileRef} type="file" accept="image/*" onChange={handleFileChange} style={{ display: 'none' }} />
                {imageFile && (
                  <p className="file-hint">
                    {imageFile.name}
                    <button type="button" onClick={() => { setImageFile(null); setImagePreview(editingProject?.image_url || null); if (fileRef.current) fileRef.current.value = ''; }}>
                      <MdClose />
                    </button>
                  </p>
                )}
              </div>

              {/* Title */}
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  placeholder="e.g. JiruuTask — Team Management App"
                  value={formData.title}
                  onChange={e => setFormData(p => ({ ...p, title: e.target.value }))}
                  required
                />
              </div>

              {/* Description */}
              <div className="form-group">
                <label>Description</label>
                <textarea
                  placeholder="Brief description of the project..."
                  value={formData.description}
                  onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
                  rows={3}
                />
              </div>

              {/* Category + Status row */}
              <div className="form-row">
                <div className="form-group">
                  <label>Category</label>
                  <select
                    value={formData.category}
                    onChange={e => setFormData(p => ({ ...p, category: e.target.value }))}
                  >
                    <option value="">— Select —</option>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={formData.status}
                    onChange={e => setFormData(p => ({ ...p, status: e.target.value }))}
                  >
                    <option value="published">Published</option>
                    <option value="draft">Draft (hidden)</option>
                  </select>
                </div>
              </div>

              {/* Tech stack */}
              <div className="form-group">
                <label><MdCode /> Tech Stack <span className="label-hint">(comma separated)</span></label>
                <input
                  type="text"
                  placeholder="e.g. React, Node.js, PostgreSQL, Cloudinary"
                  value={formData.tech_stack}
                  onChange={e => setFormData(p => ({ ...p, tech_stack: e.target.value }))}
                />
              </div>

              {/* URLs row */}
              <div className="form-row">
                <div className="form-group">
                  <label><MdPublic /> Live URL</label>
                  <input
                    type="url"
                    placeholder="https://yourapp.com"
                    value={formData.live_url}
                    onChange={e => setFormData(p => ({ ...p, live_url: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label><MdLink /> GitHub URL</label>
                  <input
                    type="url"
                    placeholder="https://github.com/..."
                    value={formData.github_url}
                    onChange={e => setFormData(p => ({ ...p, github_url: e.target.value }))}
                  />
                </div>
              </div>

              {/* Sort order */}
              <div className="form-group form-group-small">
                <label>Sort Order <span className="label-hint">(lower = first)</span></label>
                <input
                  type="number"
                  min="0"
                  value={formData.sort_order}
                  onChange={e => setFormData(p => ({ ...p, sort_order: parseInt(e.target.value) || 0 }))}
                />
              </div>

              <div className="portfolio-modal-footer">
                <button type="button" className="btn-cancel" onClick={handleCloseModal}>Cancel</button>
                <button type="submit" className="btn-save" disabled={saving}>
                  <MdUpload /> {saving ? 'Saving...' : (editingProject ? 'Update Project' : 'Add Project')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Portfolio;
